import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '../../components/layout/Layout';
import { getITAssets, createITAsset, updateITAsset, deleteITAsset, getClients, getITLocations, getITAssetCategories } from '../../lib/api';
import { getITErrorMessage } from '../../lib/itErrors';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  AssetFormDialog,
  ASSET_STATUSES,
  DEFAULT_ASSET_CATEGORIES,
  assetDisplayName,
  assetFormToPayload,
  assetStats,
  assetStatusColor,
  assetToForm,
  setAssetField,
} from '../../components/it-assets';
import { useNavigate } from 'react-router-dom';
import { Desktop, Plus, MagnifyingGlass, PencilSimple, Trash, Eye } from '@phosphor-icons/react';
import { toast } from 'sonner';

const toList = (data) => (Array.isArray(data) ? data : (data?.items || []));

export default function ITAssetsPage() {
  const navigate = useNavigate();
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editingAsset, setEditingAsset] = useState(null);
  const [form, setForm] = useState(assetToForm(null));
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterClient, setFilterClient] = useState('all');
  const [clients, setClients] = useState([]);
  const [locations, setLocations] = useState([]);
  const [categories, setCategories] = useState(DEFAULT_ASSET_CATEGORIES);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const loadAssets = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (debouncedSearch) params.search = debouncedSearch;
      if (filterCategory !== 'all') params.category = filterCategory;
      if (filterStatus !== 'all') params.status = filterStatus;
      if (filterClient !== 'all') params.client_id = filterClient;
      const res = await getITAssets(params);
      setAssets(toList(res.data));
    } catch (err) {
      toast.error(getITErrorMessage(err, 'Error al cargar los activos'));
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, filterCategory, filterStatus, filterClient]);

  useEffect(() => {
    loadAssets();
  }, [loadAssets]);

  useEffect(() => {
    (async () => {
      try {
        const [clientsRes, categoriesRes] = await Promise.all([getClients(), getITAssetCategories()]);
        setClients(toList(clientsRes.data));
        if (categoriesRes.data?.categories?.length) setCategories(categoriesRes.data.categories);
      } catch (err) {
        console.error('Error loading reference data', err);
      }
    })();
  }, []);

  const loadClientLocations = async (clientId) => {
    if (!clientId) {
      setLocations([]);
      return;
    }
    try {
      const res = await getITLocations({ client_id: clientId });
      setLocations(toList(res.data));
    } catch (err) {
      setLocations([]);
    }
  };

  const handleCreate = () => {
    setEditingAsset(null);
    setForm(assetToForm(null));
    setLocations([]);
    setShowModal(true);
  };

  const handleEdit = (asset) => {
    setEditingAsset(asset);
    setForm(assetToForm(asset));
    loadClientLocations(asset.client_id);
    setShowModal(true);
  };

  const handleDelete = async (asset) => {
    if (!window.confirm(`¿Eliminar el activo "${assetDisplayName(asset)}"? Esta acción no se puede deshacer.`)) return;
    try {
      await deleteITAsset(asset._id);
      toast.success('Activo eliminado');
      loadAssets();
    } catch (err) {
      toast.error(getITErrorMessage(err, 'No se pudo eliminar el activo'));
    }
  };

  const handleFieldChange = (path, value) => {
    setForm((prev) => {
      const next = setAssetField(prev, path, value);
      return path === 'client_id' ? { ...next, location_id: '' } : next;
    });
    if (path === 'client_id') loadClientLocations(value);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!form.category) return toast.error('La categoría es obligatoria');
    if (!form.client_id) return toast.error('Debe seleccionar un cliente');
    const payload = assetFormToPayload(form);
    if (!payload.name) return toast.error('Indica un nombre, marca/modelo, tag o serial para identificar el activo');

    setSubmitting(true);
    try {
      if (editingAsset) {
        await updateITAsset(editingAsset._id, payload);
        toast.success('Activo actualizado');
      } else {
        await createITAsset(payload);
        toast.success('Activo creado');
      }
      setShowModal(false);
      loadAssets();
    } catch (err) {
      toast.error(getITErrorMessage(err, 'Error al guardar el activo'));
    } finally {
      setSubmitting(false);
    }
  };

  const stats = assetStats(assets);
  const categoryName = (code) => categories.find((category) => category.code === code)?.name || (code || '').replace(/[_-]/g, ' ');
  const statusName = (code) => ASSET_STATUSES.find((status) => status.code === code)?.name || (code || '').replace(/_/g, ' ');

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <Desktop className="text-[#3AB8D6]" size={28} />
              Activos IT
            </h1>
            <p className="text-gray-400 text-sm mt-1">Inventario de activos tecnológicos por cliente</p>
          </div>
          <Button onClick={handleCreate} className="bg-[#E2B05E] hover:bg-[#c9993f] text-black">
            <Plus size={18} className="mr-2" /> Nuevo Activo
          </Button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: 'Total', value: stats.total, color: 'text-white' },
            { label: 'Activos', value: stats.active, color: 'text-green-400' },
            { label: 'En Reparación', value: stats.inRepair, color: 'text-yellow-400' },
            { label: 'Dados de Baja', value: stats.retired, color: 'text-gray-400' },
          ].map((stat) => (
            <div key={stat.label} className="bg-[#1a1d23] border border-gray-800 rounded-lg p-3 text-center">
              <div className={`text-xl font-bold ${stat.color}`}>{stat.value}</div>
              <div className="text-xs text-gray-500">{stat.label}</div>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[200px]">
            <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
            <Input
              placeholder="Buscar por nombre, serial, tag, hostname, marca..."
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              className="pl-9 bg-[#1a1d23] border-gray-700 text-white"
            />
          </div>
          <Select value={filterCategory} onValueChange={setFilterCategory}>
            <SelectTrigger className="w-full sm:w-[160px] bg-[#1a1d23] border-gray-700 text-white"><SelectValue placeholder="Categoría" /></SelectTrigger>
            <SelectContent className="bg-[#1a1d23] border-gray-700">
              <SelectItem value="all">Todas</SelectItem>
              {categories.map((category) => <SelectItem key={category.code} value={category.code}>{category.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-full sm:w-[160px] bg-[#1a1d23] border-gray-700 text-white"><SelectValue placeholder="Estado" /></SelectTrigger>
            <SelectContent className="bg-[#1a1d23] border-gray-700">
              <SelectItem value="all">Todos</SelectItem>
              {ASSET_STATUSES.map((status) => <SelectItem key={status.code} value={status.code}>{status.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterClient} onValueChange={setFilterClient}>
            <SelectTrigger className="w-full sm:w-[180px] bg-[#1a1d23] border-gray-700 text-white"><SelectValue placeholder="Cliente" /></SelectTrigger>
            <SelectContent className="bg-[#1a1d23] border-gray-700">
              <SelectItem value="all">Todos</SelectItem>
              {clients.map((client) => <SelectItem key={client._id} value={client._id}>{client.name || client.company_name || client.company}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div className="bg-[#1a1d23] border border-gray-800 rounded-lg overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#E2B05E]"></div>
            </div>
          ) : assets.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Desktop size={48} className="mx-auto mb-3 opacity-50" />
              <p>No hay activos que coincidan</p>
              <Button onClick={handleCreate} variant="ghost" className="mt-3 text-[#3AB8D6]">
                <Plus size={16} className="mr-1" /> Crear activo
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-800 text-xs text-gray-400 uppercase">
                    <th className="text-left p-3">Activo</th>
                    <th className="text-left p-3">Serial / Tag</th>
                    <th className="text-left p-3">Cliente</th>
                    <th className="text-left p-3">Categoría</th>
                    <th className="text-left p-3">Estado</th>
                    <th className="text-left p-3">Usuario</th>
                    <th className="text-right p-3">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {assets.map((asset) => (
                    <tr key={asset._id} className="border-b border-gray-800/50 hover:bg-[#12141a] transition-colors">
                      <td className="p-3">
                        <div className="text-sm text-white font-medium">{assetDisplayName(asset)}</div>
                        <div className="text-xs text-gray-500">{[asset.brand, asset.model].filter(Boolean).join(' ')}</div>
                      </td>
                      <td className="p-3">
                        <div className="text-sm text-gray-300">{asset.serial_number || '-'}</div>
                        <div className="text-xs text-gray-500">{asset.tag || ''}</div>
                      </td>
                      <td className="p-3">
                        <div className="text-sm text-gray-300">{asset.client_name || '-'}</div>
                        <div className="text-xs text-gray-500">{asset.location_name || ''}</div>
                      </td>
                      <td className="p-3"><span className="text-sm text-gray-300">{categoryName(asset.category)}</span></td>
                      <td className="p-3"><Badge className={assetStatusColor(asset.status)}>{statusName(asset.status)}</Badge></td>
                      <td className="p-3 text-sm text-gray-400">{asset.assigned_to || '-'}</td>
                      <td className="p-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button size="sm" variant="ghost" aria-label="Ver activo" onClick={() => navigate(`/it/assets/${asset._id}`)}>
                            <Eye size={16} className="text-[#3AB8D6]" />
                          </Button>
                          <Button size="sm" variant="ghost" aria-label="Editar activo" onClick={() => handleEdit(asset)}>
                            <PencilSimple size={16} className="text-[#E2B05E]" />
                          </Button>
                          <Button size="sm" variant="ghost" aria-label="Eliminar activo" onClick={() => handleDelete(asset)}>
                            <Trash size={16} className="text-red-400" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <AssetFormDialog
          open={showModal}
          onOpenChange={setShowModal}
          isEditing={Boolean(editingAsset)}
          form={form}
          onChange={handleFieldChange}
          onSubmit={handleSubmit}
          submitting={submitting}
          clients={clients}
          locations={locations}
          categories={categories}
        />
      </div>
    </Layout>
  );
}
